
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import {
  MailboxInterface,
  MailboxRepositoryInterface,
  MailboxStatus,
  MailboxUserCreateInterface,
  MailboxUserInterface,
  MailboxUserUpdateInterface,
} from '@/services/Mailbox/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { Website } from '@/services/Website/interfaces';
import EditMailboxUserDialog from '@/components/dialogs/mailbox/EditMailboxUserDialog.vue';
import DeleteMailboxDialog from '@/components/dialogs/mailbox/DeleteMailboxDialog.vue';
import CreateMailboxUserDialog from '@/components/dialogs/mailbox/CreateMailboxUserDialog.vue';
import DeleteMailboxUserDialog from '@/components/dialogs/mailbox/DeleteMailboxUserDialog.vue';
import ToggleMailboxDialog from '@/components/dialogs/mailbox/ToggleMailboxDialog.vue';
import SyncQuotasDialog from '@/components/dialogs/mailbox/SyncQuotasDialog.vue';

@Component({
  inject: [
    'mailboxRepository',
    'serverRepository',
  ],
  components: {
    EditMailboxUserDialog,
    DeleteMailboxDialog,
    CreateMailboxUserDialog,
    DeleteMailboxUserDialog,
    ToggleMailboxDialog,
    SyncQuotasDialog,
  },
})
export default class WebsiteMailboxesPanel extends Vue {
  @Prop({ required: true }) private readonly website!: Website;

  private mailboxRepository!: MailboxRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;

  private loading: boolean = true;
  private mailboxes: MailboxInterface[] = [];
  private serversMap: { [key: string]: ServerInterface } = {};
  private expanded: MailboxInterface[] = [];

  private mailboxesTableHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Account User', value: 'accountUser' },
    { text: 'Server', value: 'server' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
    { text: '', value: 'data-table-expand' },
  ];

  private userTableHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Address', value: 'address' },
    { text: 'Username', value: 'username' },
    { text: 'Real Name', value: 'realName' },
    { text: 'Directory', value: 'directory' },
    { text: 'Quota', value: 'quota.blockHard' },
    { text: 'Used', value: 'quota.used' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
  ];

  private mailboxStatuses: string[] = [
    'CREATING',
    'CREATED',
    'UPDATING',
    'ERROR',
    'ACTIVE',
    'DISABLED',
  ];

  private userStatuses: string[] = this.mailboxStatuses;

  private createMailboxUserDialogData: {
    mailbox: MailboxInterface;
    website: Website;
  } | false = false;
  private createMailboxUserDialog: string = '';

  private deleteMailboxDialog: string = '';
  private mailboxForDeleteDialog: MailboxInterface | false = false;

  private deleteMailboxUserDialog: string = '';
  private mailboxUserForDeleteDialog: MailboxUserInterface | false = false;

  private editMailboxUserDialogData: {
    mailbox: MailboxInterface;
    website: Website;
    user: MailboxUserInterface;
    server: ServerInterface;
  } | false = false;
  private editMailboxUserDialog: string = '';

  private toggleMailboxDialog: string = '';
  private mailboxForToggleDialog: MailboxInterface | false = false;
  private domainForToggleDialog: string | false = false;

  private syncQuotasDialog: string = '';
  private serversForSyncDialog: ServerInterface[] = [];

  public async created(): Promise<void> {
    try {
      await this.loadMailboxes();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    } finally {
      this.loading = false;
    }
  }

  private async loadMailboxes(): Promise<void> {
    const [mailboxes] = await this.mailboxRepository.searchMailbox(
      1,
      String(this.website.id),
    );
    this.mailboxes = mailboxes.filter((mailbox) => mailbox.websiteId === this.website.id);

    const serverIds = this.mailboxes.map((mailbox) => mailbox.server);
    if (serverIds.length) {
      const servers = await this.serverRepository.getServersByIds(serverIds);
      servers.forEach((server) => {
        this.serversMap[server._id] = server;
      });
    }
  }

  private async createMailbox(): Promise<void> {
    const unixUserId = this.website.details?.unixUserId;
    if (!unixUserId) {
      this.$errorDialog.show('Unix user was not found for this website.');
      return;
    }

    try {
      await this.mailboxRepository.createMailbox({
        server: this.website.originServer,
        websiteId: this.website.id,
        systemUserId: Number(unixUserId),
      });
      this.$successDialog.show('Mailbox Creation Started');
      await this.loadMailboxes();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private async editMailboxUser(user: MailboxUserInterface): Promise<void> {
    try {
      const updateData: MailboxUserUpdateInterface = {
        password: user.password ? user.password : undefined,
        realName: user.realName,
        aliases: user.aliases.map((mailboxAlias) => mailboxAlias.alias),
        forwards: user.forwards.map((forward) => forward.email),
        quota: {
          blockSoft: user.quota.blockSoft,
          blockHard: user.quota.blockHard,
        },
      };

      await this.mailboxRepository.updateMailboxUser(user.id, updateData);
      this.$successDialog.show('Mailbox user update started!');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private async deleteMailbox(mailbox: MailboxInterface): Promise<void> {
    try {
      await this.mailboxRepository.deleteMailbox(mailbox.id);
      this.mailboxes = this.mailboxes.filter((m) => m.id !== mailbox.id);
      this.$successDialog.show('Mailbox Deletion Started');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private async deleteMailboxUser(user: MailboxUserInterface): Promise<void> {
    try {
      await this.mailboxRepository.deleteMailboxUser(user.id);
      this.$successDialog.show('Mailbox User Deletion Started');
      await this.loadMailboxes();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private showCreateMailboxUserDialog(mailbox: MailboxInterface): void {
    const unixUserId = this.website.details?.unixUserId;
    if (!unixUserId) {
      this.$errorDialog.show('Unix user was not found for this website.');
      return;
    }

    this.createMailboxUserDialogData = {
      mailbox,
      website: {
        ...this.website,
        details: {
          ...this.website.details,
          unixUserId,
        },
      },
    };
    this.createMailboxUserDialog = 'create-mailbox-user-dialog';
  }

  private async createMailboxUser(userDTO: MailboxUserCreateInterface): Promise<void> {
    try {
      const user = await this.mailboxRepository.createMailboxUser(userDTO);
      const index = this.mailboxes.findIndex((mailbox) => mailbox.id === userDTO.mailboxId);
      this.$successDialog.show('Mailbox User Creation Started');
      if (index !== -1) {
        this.mailboxes[index].users.push(user);
      }
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private showEditMailboxUserDialog(id: number): void {
    let mailbox: MailboxInterface = {} as MailboxInterface;
    let user: MailboxUserInterface = {} as MailboxUserInterface;

    this.mailboxes.forEach((mb) => {
      const mailboxUser = mb.users.find((mailboxUser) => mailboxUser.id === id);
      if (!mailboxUser) {
        return;
      }
      mailbox = mb;
      user = mailboxUser;
    });

    if (!mailbox.id || !user.id) {
      return;
    }

    this.editMailboxUserDialogData = {
      mailbox,
      user,
      server: this.serversMap[mailbox.server],
      website: this.website,
    };
    this.editMailboxUserDialog = 'edit-mailbox-user-dialog';
  }

  private showDeleteMailboxDialog(id: number): void {
    const mailbox = this.mailboxes.find((m) => m.id === id);
    if (!mailbox) {
      return;
    }
    this.mailboxForDeleteDialog = mailbox;
    this.deleteMailboxDialog = 'delete-mailbox-dialog';
  }

  private showDeleteMailboxUserDialog(user: MailboxUserInterface): void {
    this.mailboxUserForDeleteDialog = user;
    this.deleteMailboxUserDialog = 'delete-mailbox-user-dialog';
  }

  private showToggleDialog(mailbox: MailboxInterface): void {
    this.toggleMailboxDialog = 'toggle-mailbox-dialog';
    this.mailboxForToggleDialog = mailbox;
    this.domainForToggleDialog = this.website.domain;
  }

  private async toggleMailbox(mailbox: MailboxInterface): Promise<void> {
    try {
      const status = [1, 4].includes(mailbox.status) ? MailboxStatus.DISABLED : MailboxStatus.ACTIVE;
      const statusStr = status === MailboxStatus.DISABLED ? 'Disable' : 'Enable';
      await this.mailboxRepository.toggleMailbox(mailbox.id, status);
      this.$successDialog.show(`Mailbox ${statusStr} Started!`);
      await this.loadMailboxes();
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }

  private async showSyncQuotesDialog(): Promise<void> {
    this.serversForSyncDialog = await this.serverRepository.getAllServers();
    this.syncQuotasDialog = 'sync-quotas-dialog';
  }

  private async syncQuotas(server: string): Promise<void> {
    try {
      await this.mailboxRepository.syncQuotas(server);
      this.$successDialog.show(`Quota sync started for server ${server}`);
    } catch (err) {
      this.$errorDialog.show(`Failed to start quotas sync for server ${server}`);
    }
  }
}
